import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { UniversalLogInput, UniversalLogLevel } from '../types/logging';
import { useUniversalLogger } from './useUniversalLogger';

type UseLogEventOptions = {
  source?: UniversalLogInput['source'];
  page?: string;
  defaultDetails?: Record<string, unknown>;
  /** Optional module identifier for categorizing logs */
  module?: string;
  /** Optional entity ID for context */
  entityId?: string;
};

type LogEventHandler = (message: string, details?: Record<string, unknown>) => void;

export function useLogEvent(baseCategory: string, options: UseLogEventOptions = {}) {
  const location = useLocation();
  const { log } = useUniversalLogger();

  const fallbackPage = `${location.pathname}${location.search || ''}${location.hash || ''}`;
  const resolvedPage = options.page ?? (fallbackPage || undefined);
  const resolvedSource = options.source ?? 'frontend';
  const defaults = options.defaultDetails;

  const logEvent = useCallback(
    (level: UniversalLogLevel, message: string, details?: Record<string, unknown>) => {
      log({
        level,
        category: baseCategory,
        source: resolvedSource,
        page: resolvedPage,
        message,
        details: defaults || details ? { ...(defaults ?? {}), ...(details ?? {}) } : undefined,
      });
    },
    [baseCategory, defaults, log, resolvedPage, resolvedSource]
  );

  const logInfo: LogEventHandler = useCallback(
    (message, details) => logEvent('info', message, details),
    [logEvent]
  );

  const logWarn: LogEventHandler = useCallback(
    (message, details) => logEvent('warn', message, details),
    [logEvent]
  );

  const logError: LogEventHandler = useCallback(
    (message, details) => logEvent('error', message, details),
    [logEvent]
  );

  const logDebug: LogEventHandler = useCallback(
    (message, details) => logEvent('debug', message, details),
    [logEvent]
  );

  return useMemo(
    () => ({
      logEvent,
      logInfo,
      logWarn,
      logError,
      logDebug,
    }),
    [logDebug, logError, logEvent, logInfo, logWarn]
  );
}
